import React from "react";
import cx from "classnames";
import {
  Clamp,
  CloseIcon,
  FlucsDownIcon,
  FlucsUpIcon,
  LockIcon,
  Odds,
  UpOrDown,
} from "@picklebet/ui";
import { OutcomeIcon } from "@/features/shell/components/shared/OutcomeIcon";
import { TitleIcon } from "@/features/shell/components/shared/TitleIcon";
import { useTitle } from "@/hooks/graphql/useTitles";
import {
  isBettingEvent,
  isRacingEvent,
  useEvent,
} from "@/hooks/firestore/betting/useEvent";
import { StreamButton } from "@/features/shell/components/shared/StreamButton";
import {
  EventStatus,
  StatusEnum,
} from "@/features/shell/components/shared/EventStatus";
import { isPast } from "date-fns";

import { getRaceMarketName } from "@/features/pickems/util/helpers";
import type { OddsFormat } from "types/BetTypes";
import type { PickslipPick } from "@/features/pickems/types/PickslipPick";

import * as styles from "./PickSlipPick.module.scss";

type PickSlipPickProps = {
  selection: PickslipPick;
  oddsFormat: OddsFormat;
  doRemoveSelection: () => void;
  canClose?: boolean;
  isSRM?: boolean;
  eventId?: string;
  oddsIncreased?: boolean;
  oddsDecreased?: boolean;
  isScratched?: boolean;
};

const PickSlipPick = (props: PickSlipPickProps) => {
  const {
    selection,
    oddsFormat,
    doRemoveSelection,
    eventId,
    oddsIncreased,
    oddsDecreased,
    isScratched = false,
  } = props;

  const {
    eventName,
    selectionName,
    oddsChanged,
    freshOdds,
    lockedOdds,
    outcome,
    competitor,
  } = selection;

  const { event } = useEvent(eventId, "all");
  const hasStarted = event && isPast(event.scheduledStartTime);
  const isSuspended = event?.status === StatusEnum.SUSPENDED && !hasStarted;
  if (event?.status === StatusEnum.SUSPENDED && hasStarted) {
    event.status = StatusEnum.LIVE;
  }
  const title = useTitle(event?.sport.toLowerCase() ?? "");
  const isRaceEvent = isRacingEvent(event);
  const raceMarketName = isRaceEvent && getRaceMarketName(outcome?.type);

  const compareToPriorSelection = () => {
    if (oddsIncreased) return "up";
    if (oddsDecreased) return "down";
    return undefined;
  };

  return (
    <UpOrDown
      value={oddsChanged ? Number(freshOdds) : Number(lockedOdds)}
      className={cx(styles.pickContent)}
      animationLength={oddsChanged ? null : 3000}
      oddsChangeDirectionOverride={compareToPriorSelection()}
    >
      <div className={styles.header}>
        <div className={styles.icon}>
          <TitleIcon title={title} />
        </div>
        <div className={styles.title}>
          <div className={styles.selectionName}>
            <OutcomeIcon
              className={styles.outcomeIcon}
              outcomeType={outcome?.type}
              competitor={competitor}
              silksUrl={isRaceEvent ? event.silksUrl : undefined}
            />
            <div className={cx({ [styles.scratched]: isScratched })}>
              {selectionName}
            </div>
          </div>
          <div className={styles.eventNamePickSlip}>{eventName}</div>
          <div className={styles.oddsWrapper}>
            <>
              {raceMarketName ? `${raceMarketName} -` : ""}
              {oddsChanged && (
                <Odds
                  className={cx(styles.odds, styles.oldOdds, {
                    [styles.up]: Number(freshOdds) > Number(lockedOdds),
                    [styles.down]: Number(freshOdds) < Number(lockedOdds),
                  })}
                  base={lockedOdds || 0}
                  format={oddsFormat}
                />
              )}
              <Odds
                className={styles.odds}
                base={oddsChanged ? freshOdds || 0 : lockedOdds || 0}
                format={oddsFormat}
              />
            </>
            <div className={styles.arrowUp}>
              <FlucsUpIcon />
            </div>
            <div className={styles.arrowDown}>
              <FlucsDownIcon />
            </div>
          </div>
        </div>
        <div className={styles.clamp}>
          <Clamp
            variant={`grey`}
            onClick={doRemoveSelection}
            className={cx(styles.close)}
          >
            <CloseIcon />
          </Clamp>

          <div className={styles.statusWithStream}>
            {event &&
              isBettingEvent(event) &&
              [StatusEnum.ACTIVE, StatusEnum.LIVE].includes(event.status) && (
                <StreamButton
                  title={event.eventName}
                  sport={event.sport}
                  url={event.streamUri ?? ""}
                  startTime={event.nextBetStop ?? event.scheduledStartTime}
                  eventId={event.eventId}
                  isPlaceholder={event.attributes?.streamExpected}
                  hideIfNotThirtyMinutesBeforeStart
                />
              )}
            {isSuspended ? (
              <Clamp variant="disabled">
                <LockIcon className={styles.lockIcon} />
              </Clamp>
            ) : (
              event && (
                <EventStatus
                  status={event.status as any}
                  scheduledStartTime={
                    isBettingEvent(event)
                      ? event.nextBetStop || event.scheduledStartTime
                      : event.scheduledStartTime
                  }
                />
              )
            )}
          </div>
        </div>
      </div>
    </UpOrDown>
  );
};

export { PickSlipPick };
